/**
 * PeriodFilter Component
 * Reusable period selector for report pages
 * Allows override of global fiscal period settings
 */

import React, { useState, useEffect } from 'react';
import { Calendar, ChevronDown, Check, X } from 'lucide-react';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import { formatDateToISO } from '../../utils/formatters';

const QUICK_PERIODS = [
  { value: 'global', label: 'Use Global Period', icon: '🌐' },
  { value: 'today', label: 'Today', icon: '📅' },
  { value: 'yesterday', label: 'Yesterday', icon: '⏮️' },
  { value: 'this_week', label: 'This Week', icon: '📆' },
  { value: 'last_week', label: 'Last Week', icon: '⏪' },
  { value: 'this_month', label: 'This Month', icon: '📊' },
  { value: 'last_month', label: 'Last Month', icon: '📉' },
  { value: 'this_quarter', label: 'This Quarter', icon: '📈' },
  { value: 'this_year', label: 'This Year', icon: '🗓️' },
  { value: 'all_time', label: 'All Time', icon: '♾️' },
  { value: 'custom', label: 'Custom Range', icon: '✏️' }
];

const PeriodFilter = ({
  onChange,
  showLabel = true,
  compact = false,
  className = '',
  storageKey = null // Optional key for localStorage persistence
}) => {
  const { periodRange, settings } = useFiscalPeriod();

  // Load persisted state from localStorage if storageKey is provided
  const getInitialState = () => {
    if (!storageKey) return { period: 'global', customStart: '', customEnd: '' };
    try {
      const saved = localStorage.getItem(`period_filter_${storageKey}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          period: parsed.period || 'global',
          customStart: parsed.customStart || '',
          customEnd: parsed.customEnd || ''
        };
      }
    } catch (e) {
      console.warn('Failed to load period filter state:', e);
    }
    return { period: 'global', customStart: '', customEnd: '' };
  };

  const initialState = getInitialState();
  const [selectedPeriod, setSelectedPeriod] = useState(initialState.period);
  const [customStart, setCustomStart] = useState(initialState.customStart);
  const [customEnd, setCustomEnd] = useState(initialState.customEnd);
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  // Persist state to localStorage when it changes
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(
          `period_filter_${storageKey}`,
          JSON.stringify({
            period: selectedPeriod,
            customStart,
            customEnd
          })
        );
      } catch (e) {
        console.warn('Failed to persist period filter state:', e);
      }
    }
  }, [storageKey, selectedPeriod, customStart, customEnd]);

  // Calculate date range based on selected period
  const calculateDateRange = (period) => {
    const today = new Date();
    let start = null;
    let end = null;
    let label = '';

    switch (period) {
      case 'global':
        // Use global fiscal period range
        return {
          start_date: periodRange?.start_date || null,
          end_date: periodRange?.end_date || null,
          label: periodRange?.period_label || 'Global Period',
          isGlobal: true
        };

      case 'today':
        start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        label = 'Today';
        break;

      case 'yesterday':
        start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        label = 'Yesterday';
        break;

      case 'this_week':
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        start = new Date(today);
        start.setDate(today.getDate() + mondayOffset);
        start.setHours(0, 0, 0, 0); // Start of local Monday
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999); // End of local Sunday
        label = 'This Week';
        break;

      case 'last_week':
        const lastWeekDay = today.getDay();
        const lastMondayOffset = lastWeekDay === 0 ? -13 : -6 - lastWeekDay;
        start = new Date(today);
        start.setDate(today.getDate() + lastMondayOffset);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        label = 'Last Week';
        break;

      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        label = start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        break;

      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        label = start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        break;

      case 'this_quarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
        label = `Q${quarter + 1} ${today.getFullYear()}`;
        break;

      case 'this_year':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        label = `${today.getFullYear()}`;
        break;

      case 'all_time':
        return { start_date: null, end_date: null, label: 'All Time', isGlobal: false };

      case 'custom':
        if (customStart && customEnd) {
          return {
            start_date: customStart,
            end_date: customEnd,
            label: `${new Date(customStart).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date(customEnd).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}`,
            isGlobal: false
          };
        }
        return { start_date: null, end_date: null, label: 'Custom', isGlobal: false };

      default:
        return { start_date: null, end_date: null, label: 'All Time', isGlobal: false };
    }

    return {
      start_date: formatDateToISO(start),
      end_date: formatDateToISO(end),
      label,
      isGlobal: false
    };
  };

  // Handle period selection
  const handlePeriodSelect = (period) => {
    setSelectedPeriod(period);

    if (period === 'custom') {
      setShowCustomPicker(true);
      setIsOpen(false);
      return;
    }

    setShowCustomPicker(false);
    setIsOpen(false);

    const dateRange = calculateDateRange(period);
    if (onChange) {
      onChange(dateRange);
    }
  };

  // Handle custom date apply
  const handleCustomApply = () => {
    if (!customStart || !customEnd) return;

    const dateRange = calculateDateRange('custom');
    setShowCustomPicker(false);
    if (onChange) {
      onChange(dateRange);
    }
  };

  // Get current display text
  const getDisplayText = () => {
    if (selectedPeriod === 'global') {
      return periodRange?.period_label || 'Global Period';
    }
    if (selectedPeriod === 'custom' && customStart && customEnd) {
      return `${new Date(customStart).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date(customEnd).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
    }
    const period = QUICK_PERIODS.find((p) => p.value === selectedPeriod);
    return period?.label || 'Select Period';
  };

  // Initialize on mount and when global period changes
  useEffect(() => {
    if (onChange) {
      // Fire onChange with the current selected period (either persisted or global)
      onChange(calculateDateRange(selectedPeriod));
    }
  }, [periodRange]); // Re-fire when global period changes (for 'global' selection)

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 bg-theme-secondary border border-theme rounded-lg hover:border-accent-gold transition-colors ${compact ? 'text-sm' : ''}`}
      >
        <Calendar className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-accent-gold`} />
        {showLabel && <span className="text-theme-primary font-medium">{getDisplayText()}</span>}
        <ChevronDown
          className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-theme-muted ${isOpen ? 'rotate-180' : ''} transition-transform`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-theme-secondary rounded-lg shadow-lg border border-theme z-50 py-2 max-h-80 overflow-y-auto">
            {QUICK_PERIODS.map((period) => (
              <button
                key={period.value}
                onClick={() => handlePeriodSelect(period.value)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-theme-tertiary transition-colors ${
                  selectedPeriod === period.value
                    ? 'bg-accent-gold/10 text-accent-gold'
                    : 'text-theme-primary'
                }`}
              >
                <span>{period.icon}</span>
                <span className="flex-1 text-sm">{period.label}</span>
                {selectedPeriod === period.value && <Check className="h-4 w-4 text-accent-gold" />}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Custom Date Picker Modal */}
      {showCustomPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-theme-secondary rounded-lg shadow-lg w-full max-w-sm">
            <div className="p-4 border-b border-theme flex items-center justify-between">
              <h3 className="font-semibold text-theme-primary">Custom Date Range</h3>
              <button
                onClick={() => setShowCustomPicker(false)}
                className="p-1 hover:bg-theme-tertiary rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-3 py-2 border border-theme rounded-lg bg-theme-secondary text-theme-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-theme rounded-lg bg-theme-secondary text-theme-primary"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowCustomPicker(false)}
                  className="flex-1 px-4 py-2 border border-theme rounded-lg hover:bg-theme-tertiary text-theme-primary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomApply}
                  disabled={!customStart || !customEnd}
                  className="flex-1 px-4 py-2 bg-accent-gold text-black font-medium rounded-lg hover:bg-accent-goldDark disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodFilter;
